import { useState, useMemo, useEffect } from 'react';
import {
    Box, Button, TextField, Stack, Typography, Alert, CircularProgress, Paper, Autocomplete,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination
} from '@mui/material';
import * as XLSX from 'xlsx';
import { UrlApi } from '../../../services/apirest';
import Navbar from '../../Navbar';


const COLUMNAS = [
    { key: 'sucursal', titulo: 'Sucursal', ancho: 18 },
    { key: 'fecha_pedido', titulo: 'Fecha pedido', ancho: 17, tipo: 'fecha' },
    { key: 'chofer', titulo: 'Chofer', ancho: 24 },
    { key: 'cliente', titulo: 'Cliente', ancho: 32 },
    { key: 'pedido', titulo: 'Pedido', ancho: 10, tipo: 'numero' },
    { key: 'orden', titulo: 'Orden', ancho: 14 },
    { key: 'detalle', titulo: 'Detalle', ancho: 30 },
    { key: 'medio_de_pago', titulo: 'Medio de pago', ancho: 18 },
    { key: 'letra', titulo: 'Letra', ancho: 6 },
    { key: 'pto_venta', titulo: 'Pto. venta', ancho: 10 },
    { key: 'nro_comprobante', titulo: 'Nro. comprobante', ancho: 16 },
    { key: 'importe', titulo: 'Importe', ancho: 14, tipo: 'importe' },
    { key: 'rendicion', titulo: 'Rendición', ancho: 12 },
    { key: 'fecha_registro_pago', titulo: 'Fecha registro pago', ancho: 19, tipo: 'fecha' },
];

const FMT_FECHA_EXCEL = 'dd/mm/yyyy hh:mm';
const FMT_IMPORTE_EXCEL = '#,##0.00';
const FILAS_POR_PAGINA_OPCIONES = [50, 100, 250, 500];
const DEMORA_BUSQUEDA_MS = 300;
const MEDIOS_POR_DEFECTO = [6, 11, 12, 13]; // QR, QR Mercado Pago, Link de Pago MP, PosNet MP

const fmtImporte = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function hoyISO() {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
}

// 'yyyy-mm-dd hh:mm:ss' -> 'dd/mm/yyyy hh:mm'
function fechaPantalla(s) {
    if (!s) return '';
    const [f, h = ''] = s.split(' ');
    const [y, m, d] = f.split('-');
    return `${d}/${m}/${y} ${h.slice(0, 5)}`.trim();
}

// 'yyyy-mm-dd hh:mm:ss' -> número de serie de Excel (sin efecto de zona horaria)
function fechaSerialExcel(s) {
    if (!s) return null;
    const [f, h = '00:00:00'] = s.split(' ');
    const [y, m, d] = f.split('-').map(Number);
    const [hh, mi, ss] = h.split(':').map(Number);
    const ms = Date.UTC(y, m - 1, d, hh, mi, ss || 0) - Date.UTC(1899, 11, 30);
    return ms / 86400000;
}

// Minúsculas y sin acentos, para buscar sin importar cómo se escriba
function normalizar(s) {
    return String(s ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

// Texto de búsqueda de una fila: valores crudos + como se ven en pantalla
function textoBusquedaFila(f) {
    return normalizar(
        COLUMNAS.map(c => {
            const v = f[c.key];
            if (v === null || v === undefined || v === '') return '';
            if (c.tipo === 'fecha') return `${v} ${fechaPantalla(v)}`;
            if (c.tipo === 'importe') return `${v} ${fmtImporte.format(Number(v))}`;
            return v;
        }).join(' | ')
    );
}

export default function PagosEnvasadoReporte() {
    const [desde, setDesde] = useState(hoyISO());
    const [hasta, setHasta] = useState(hoyISO());
    const [filas, setFilas] = useState([]);
    const [rango, setRango] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState('');
    const [pagina, setPagina] = useState(0);
    const [filasPorPagina, setFilasPorPagina] = useState(100);
    const [busqueda, setBusqueda] = useState('');
    const [busquedaAplicada, setBusquedaAplicada] = useState('');
    const [medios, setMedios] = useState([]);
    const [mediosSeleccionados, setMediosSeleccionados] = useState([]);
    const [cargandoMedios, setCargandoMedios] = useState(true);

    // Carga la lista de medios de pago al abrir la pantalla
    useEffect(() => {
        let cancelado = false;
        (async () => {
            try {
                const r = await fetch(`${UrlApi}/pagosenvasados/medios`);
                const data = await r.json().catch(() => null);
                if (!r.ok) throw new Error(data?.error || data?.sqlMessage || `Error ${r.status}`);
                if (cancelado) return;
                const lista = data || [];
                setMedios(lista);
                setMediosSeleccionados(lista.filter(m => MEDIOS_POR_DEFECTO.includes(m.id)));
            } catch (e) {
                if (!cancelado) setError(`No se pudieron cargar los medios de pago: ${e.message}`);
            } finally {
                if (!cancelado) setCargandoMedios(false);
            }
        })();
        return () => { cancelado = true; };
    }, []);

    // Aplica la búsqueda cuando el usuario deja de tipear
    useEffect(() => {
        const t = setTimeout(() => {
            setBusquedaAplicada(busqueda);
            setPagina(0);
        }, DEMORA_BUSQUEDA_MS);
        return () => clearTimeout(t);
    }, [busqueda]);

    // Índice de búsqueda: se arma una sola vez por cada consulta al backend
    const indiceBusqueda = useMemo(() => filas.map(textoBusquedaFila), [filas]);

    const filasFiltradas = useMemo(() => {
        const terminos = normalizar(busquedaAplicada).split(/\s+/).filter(Boolean);
        if (!terminos.length) return filas;
        return filas.filter((_, i) => terminos.every(t => indiceBusqueda[i].includes(t)));
    }, [filas, indiceBusqueda, busquedaAplicada]);

    const hayFiltro = filasFiltradas.length !== filas.length;

    const total = useMemo(
        () => filasFiltradas.reduce((acc, f) => acc + Number(f.importe || 0), 0),
        [filasFiltradas]
    );

    // Solo las filas de la página actual se dibujan
    const filasPagina = useMemo(
        () => filasFiltradas.slice(pagina * filasPorPagina, pagina * filasPorPagina + filasPorPagina),
        [filasFiltradas, pagina, filasPorPagina]
    );

    async function buscar() {
        setError('');
        if (!desde || !hasta) return setError('Completá las dos fechas');
        if (hasta < desde) return setError('La fecha hasta no puede ser menor que desde');

        const ids = mediosSeleccionados.map(m => m.id);
        const qs = ids.length ? `?medios=${ids.join(',')}` : '';

        setCargando(true);
        try {
            const r = await fetch(`${UrlApi}/pagosenvasados/${desde}/${hasta}${qs}`);
            const data = await r.json().catch(() => null);
            if (!r.ok) throw new Error(data?.error || data?.sqlMessage || `Error ${r.status}`);
            setFilas(data || []);
            setRango({ desde, hasta });
            setPagina(0);
        } catch (e) {
            setError(e.message);
            setFilas([]);
            setRango(null);
            setPagina(0);
        } finally {
            setCargando(false);
        }
    }

    function cambiarPagina(_e, nuevaPagina) {
        setPagina(nuevaPagina);
    }

    function cambiarFilasPorPagina(e) {
        setFilasPorPagina(parseInt(e.target.value, 10));
        setPagina(0);
    }

    function descargarExcel() {
        const datos = filasFiltradas;
        const idxImp = COLUMNAS.findIndex(c => c.key === 'importe'); // índice base 0

        // Encabezados + datos
        const aoa = [COLUMNAS.map(c => c.titulo)];
        datos.forEach(f => {
            aoa.push(COLUMNAS.map(c => {
                const v = f[c.key];
                if (c.tipo === 'fecha') return fechaSerialExcel(v);
                if (c.tipo === 'importe' || c.tipo === 'numero') {
                    return v === null || v === undefined || v === '' ? null : Number(v);
                }
                return v ?? '';
            }));
        });

        // Fila de total
        const filaTotal = COLUMNAS.map(() => null);
        filaTotal[idxImp - 1] = 'Total';
        filaTotal[idxImp] = total;
        aoa.push(filaTotal);

        const ws = XLSX.utils.aoa_to_sheet(aoa);

        // Formatos de fecha e importe en cada celda de datos
        for (let r = 1; r <= datos.length; r++) {
            COLUMNAS.forEach((c, ci) => {
                const celda = ws[XLSX.utils.encode_cell({ r, c: ci })];
                if (!celda) return;
                if (c.tipo === 'fecha') celda.z = FMT_FECHA_EXCEL;
                if (c.tipo === 'importe') celda.z = FMT_IMPORTE_EXCEL;
            });
        }

        // Fórmula del total
        const filaTotalIdx = datos.length + 1;
        const letra = XLSX.utils.encode_col(idxImp);
        ws[XLSX.utils.encode_cell({ r: filaTotalIdx, c: idxImp })] = {
            t: 'n',
            v: total,
            f: `SUM(${letra}2:${letra}${datos.length + 1})`,
            z: FMT_IMPORTE_EXCEL,
        };

        // Ancho de columnas y autofiltro
        ws['!cols'] = COLUMNAS.map(c => ({ wch: c.ancho }));
        ws['!autofilter'] = {
            ref: XLSX.utils.encode_range({
                s: { r: 0, c: 0 },
                e: { r: datos.length, c: COLUMNAS.length - 1 },
            }),
        };

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Pagos envasado');
        const sufijo = hayFiltro ? '_filtrado' : '';
        XLSX.writeFile(wb, `pagos_envasado_${rango.desde}_a_${rango.hasta}${sufijo}.xlsx`);
    }

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Pagos envasado</Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                    <TextField
                        label="Desde" type="date" size="small" value={desde}
                        onChange={e => setDesde(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Hasta" type="date" size="small" value={hasta}
                        onChange={e => setHasta(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        sx={{ minWidth: 320, maxWidth: 600, flex: 1 }}
                        options={medios}
                        value={mediosSeleccionados}
                        onChange={(_e, v) => setMediosSeleccionados(v)}
                        getOptionLabel={o => o.nombre}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        disableCloseOnSelect
                        loading={cargandoMedios}
                        limitTags={4}
                        noOptionsText="Sin medios de pago"
                        renderInput={params => (
                            <TextField
                                {...params}
                                label="Medios de pago"
                                placeholder={mediosSeleccionados.length ? '' : 'Todos'}
                                helperText={mediosSeleccionados.length ? '' : 'Sin selección se incluyen todos'}
                            />
                        )}
                    />
                    <Button variant="contained" onClick={buscar} disabled={cargando || cargandoMedios}>
                        {cargando ? <CircularProgress size={22} /> : 'Buscar'}
                    </Button>
                    <Button variant="outlined" onClick={descargarExcel} disabled={!filasFiltradas.length || cargando}>
                        {hayFiltro ? 'Descargar Excel (filtrado)' : 'Descargar Excel'}
                    </Button>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {filas.length > 0 && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 1 }} alignItems={{ sm: 'center' }}>
                        <TextField
                            type="search"
                            size="small"
                            label="Buscar en la tabla"
                            placeholder="cliente, chofer, comprobante, medio de pago..."
                            value={busqueda}
                            onChange={e => setBusqueda(e.target.value)}
                            sx={{ minWidth: { sm: 380 } }}
                        />
                        <Typography variant="body2">
                            {hayFiltro
                                ? `${filasFiltradas.length.toLocaleString('es-AR')} de ${filas.length.toLocaleString('es-AR')} registros`
                                : `${filas.length.toLocaleString('es-AR')} registros`}
                            {' — Total: '}<b>$ {fmtImporte.format(total)}</b>
                        </Typography>
                    </Stack>
                )}

                {filas.length > 0 && filasFiltradas.length === 0 && (
                    <Alert severity="info">Ninguna fila coincide con la búsqueda.</Alert>
                )}

                {filasFiltradas.length > 0 && (
                    <Paper>
                        <TableContainer sx={{ maxHeight: '65vh' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {COLUMNAS.map(c => (
                                            <TableCell key={c.key} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
                                                align={c.tipo === 'importe' ? 'right' : 'left'}>
                                                {c.titulo}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filasPagina.map((f, i) => (
                                        <TableRow key={pagina * filasPorPagina + i} hover>
                                            {COLUMNAS.map(c => (
                                                <TableCell key={c.key} sx={{ whiteSpace: 'nowrap' }}
                                                    align={c.tipo === 'importe' ? 'right' : 'left'}>
                                                    {c.tipo === 'fecha' ? fechaPantalla(f[c.key])
                                                        : c.tipo === 'importe' ? fmtImporte.format(Number(f[c.key] || 0))
                                                            : f[c.key] ?? ''}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={filasFiltradas.length}
                            page={pagina}
                            onPageChange={cambiarPagina}
                            rowsPerPage={filasPorPagina}
                            onRowsPerPageChange={cambiarFilasPorPagina}
                            rowsPerPageOptions={FILAS_POR_PAGINA_OPCIONES}
                            labelRowsPerPage="Filas por página"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from.toLocaleString('es-AR')}–${to.toLocaleString('es-AR')} de ${count.toLocaleString('es-AR')}`
                            }
                            showFirstButton
                            showLastButton
                        />
                    </Paper>
                )}

                {rango && !filas.length && !error && (
                    <Alert severity="info">Sin resultados para el período.</Alert>
                )}
            </Box>
        </>
    );
}